import { useCallback, useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/services/api/auth'
import { cookieAuthEnabled } from '@/services/cookieAuthFlag'
import type { LoginRequest, ChangePasswordRequest } from '@/types/auth'

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
}

// Global flag to prevent multiple initializations across hook instances
let globalInitCalled = false

/**
 * Hook for managing authentication state and actions
 */
export function useAuth() {
  const {
    user,
    token,
    isAuthenticated,
    isLoading,
    error,
    login: storeLogin,
    logout: storeLogout,
    hasPermission,
    hasRole,
    initialize,
    setError,
  } = useAuthStore()

  const queryClient = useQueryClient()
  const initCalledRef = useRef(false)

  // Initialize auth on mount - with guards against multiple calls
  useEffect(() => {
    // Skip if already called in this hook instance or globally
    if (initCalledRef.current || globalInitCalled) {
      return
    }
    initCalledRef.current = true
    globalInitCalled = true
    initialize()
  }, [initialize]) // initialize is stable from Zustand store

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: LoginRequest) => {
      await storeLogin(email, password)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.all })
    },
    onError: (error: { message?: string }) => {
      setError(error.message || 'Login failed')
    },
  })

  // Logout handler
  const logout = useCallback(() => {
    // authStore.logout resets the query cache itself (resetClientState, #301).
    // Returns the server-revoke outcome; callers that navigate must await it.
    return storeLogout()
  }, [storeLogout])

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: ChangePasswordRequest) => {
      await authApi.changePassword(data)
    },
  })

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: { fullName?: string; avatarUrl?: string }) => {
      return authApi.updateProfile(data)
    },
    onSuccess: (response) => {
      if (response.success && response.user) {
        useAuthStore.getState().updateUser(response.user)
      }
      queryClient.invalidateQueries({ queryKey: authKeys.me() })
    },
  })

  return {
    // State
    user,
    token,
    isAuthenticated,
    isLoading: isLoading || loginMutation.isPending,
    error,

    // Actions
    login: loginMutation.mutateAsync,
    logout,
    changePassword: changePasswordMutation.mutateAsync,
    updateProfile: updateProfileMutation.mutateAsync,

    // Mutations
    loginMutation,
    changePasswordMutation,
    updateProfileMutation,

    // Permission helpers
    hasPermission,
    hasRole,
  }
}

/**
 * Hook for fetching current user
 */
export function useCurrentUser() {
  const { isAuthenticated, token } = useAuthStore()

  return useQuery({
    queryKey: authKeys.me(),
    queryFn: () => authApi.me(),
    // WS-2 cookie-only mode: there is no JS-readable token, so gate on the
    // authenticated session alone — /auth/me authenticates via the HttpOnly
    // cookie. Default (flag off) keeps the token requirement.
    enabled: isAuthenticated && (!!token || cookieAuthEnabled()),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  })
}

/**
 * Hook for requiring a specific permission
 */
export function useRequirePermission(permission: string) {
  const { hasPermission, isAuthenticated } = useAuth()

  return {
    hasPermission: hasPermission(permission),
    isAuthenticated,
  }
}

/**
 * Hook for requiring a specific role
 */
export function useRequireRole(role: Parameters<ReturnType<typeof useAuth>['hasRole']>[0]) {
  const { hasRole, isAuthenticated } = useAuth()

  return {
    hasRole: hasRole(role),
    isAuthenticated,
  }
}
